import React from "react";

// ---- KPI stat card (gradient stripe + delta line) --------------------------
export function StatCard({ label, value, stripe = "green", delta, deltaDirection }) {
  const grad = stripe === "blue" ? "var(--mt-grad-blue)" : "var(--mt-grad-green)";
  const isDown = deltaDirection === "down";
  const deltaColor = deltaDirection
    ? isDown
      ? "#e5484d"
      : "var(--mt-theme-green)"
    : "var(--mt-muted)";
  return (
    <div
      className="mt-card"
      style={{
        position: "relative",
        padding: "16px 16px 14px",
        overflow: "hidden",
        border: "1px solid var(--mt-border)",
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      <span
        style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: grad }}
      />
      <span
        style={{
          fontSize: "var(--mt-text-xs)",
          color: "var(--mt-muted)",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.03em",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: "var(--mt-text-2xl)",
          fontWeight: 800,
          color: "var(--mt-ink)",
          lineHeight: 1.1,
        }}
      >
        {value}
      </span>
      {delta && (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontSize: "var(--mt-text-xs)",
            color: deltaColor,
          }}
        >
          {deltaDirection && <i className={`bi bi-arrow-${isDown ? "down" : "up"}-short`} />}
          {delta}
        </span>
      )}
    </div>
  );
}

// ---- Badge (inline label pill) ---------------------------------------------
const BADGE_TONES = {
  purple: { bg: "#f3e5f5", fg: "#9c27b0" },
  red: { bg: "var(--mt-theme-red-bg)", fg: "var(--mt-theme-red)" },
  green: { bg: "#e8f5e9", fg: "#2e7d32" },
};
export function Badge({ tone = "red", children }) {
  const t = BADGE_TONES[tone] || BADGE_TONES.red;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "1px 7px",
        borderRadius: 999,
        fontSize: "var(--mt-text-xs)",
        fontWeight: 700,
        background: t.bg,
        color: t.fg,
      }}
    >
      {children}
    </span>
  );
}

// ---- Status pill (agenda due state) ----------------------------------------
const PILL_TONES = {
  danger: { bg: "#fdecec", fg: "#e5484d" },
  warning: { bg: "#fff4e5", fg: "#b76e00" },
};
export function StatusPill({ tone = "warning", children }) {
  const t = PILL_TONES[tone] || PILL_TONES.warning;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 9px",
        borderRadius: 999,
        fontSize: "var(--mt-text-xs)",
        fontWeight: 700,
        background: t.bg,
        color: t.fg,
      }}
    >
      {children}
    </span>
  );
}

// ---- Role segmented control (Manager / Agent / Admin) ----------------------
const ROLES = [
  { key: "manager", label: "Manager", icon: "people-fill" },
  { key: "agent", label: "Agent", icon: "person-badge" },
  { key: "admin", label: "Admin", icon: "shield-check" },
];
export function RoleSegmentedControl({ value, onChange, allowedRoles }) {
  const roles = allowedRoles
    ? ROLES.filter((r) => allowedRoles.includes(r.key))
    : ROLES;
  // a single available role isn't a choice — don't render a toggle
  if (roles.length < 2) return null;
  return (
    <div
      role="tablist"
      aria-label="Dashboard role"
      style={{
        display: "inline-flex",
        padding: 3,
        gap: 2,
        background: "var(--mt-gray-100)",
        borderRadius: "var(--mt-radius-pill)",
        border: "1px solid var(--mt-border)",
      }}
    >
      {roles.map((r) => {
        const active = value === r.key;
        return (
          <button
            key={r.key}
            type="button"
            role="tab"
            aria-selected={active}
            className="mt-click"
            onClick={() => onChange(r.key)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              border: "none",
              cursor: "pointer",
              padding: "6px 14px",
              borderRadius: "var(--mt-radius-pill)",
              fontFamily: "inherit",
              fontSize: "var(--mt-text-sm)",
              fontWeight: 700,
              background: active ? "var(--mt-theme-red)" : "transparent",
              color: active ? "#fff" : "var(--mt-muted)",
              boxShadow: active ? "0 2px 6px rgba(244,67,54,0.35)" : "none",
              transition: "all var(--mt-dur-fast) var(--mt-ease-out)",
            }}
          >
            <i className={"bi bi-" + r.icon} aria-hidden="true" />
            <span>{r.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ---- Period control (shared by Lead Dashboard + Lead Activity) --------------
export const PERIODS = [
  ["today", "Today"],
  ["yesterday", "Yesterday"],
  ["7d", "7 days"],
  ["30d", "30 days"],
];
export const PERIOD_LABEL = {
  today: "today",
  yesterday: "yesterday",
  "7d": "last 7 days",
  "30d": "last 30 days",
};

export function PeriodControl({ value, onChange }) {
  return (
    <div
      role="group"
      aria-label="Time period"
      style={{
        display: "inline-flex",
        padding: 3,
        gap: 2,
        background: "var(--mt-gray-100)",
        border: "1px solid var(--mt-border)",
        borderRadius: "var(--mt-radius-pill)",
      }}
    >
      {PERIODS.map(([k, l]) => {
        const active = value === k;
        return (
          <button
            key={k}
            type="button"
            className="mt-click"
            aria-pressed={active}
            onClick={() => onChange(k)}
            style={{
              border: "none",
              cursor: "pointer",
              padding: "6px 13px",
              borderRadius: "var(--mt-radius-pill)",
              fontFamily: "inherit",
              fontSize: "var(--mt-text-sm)",
              fontWeight: 700,
              background: active ? "var(--mt-surface)" : "transparent",
              color: active ? "var(--mt-theme-red)" : "var(--mt-muted)",
              boxShadow: active ? "var(--mt-shadow-card)" : "none",
            }}
          >
            {l}
          </button>
        );
      })}
    </div>
  );
}
